/**
 * Crawl (ticker) design-store catalog. Mirrors the other widget catalogs, but a
 * crawl SCROLLS, so a design is split into two parts:
 *  - the FIXED bar chrome (background + left "tag" chip). Rendered once as a
 *    sprite stretched to the widget bounds.
 *  - the SCROLL text strip — the user's crawl text in the design's typography +
 *    a coloured separator, rasterised to its natural width and animated
 *    horizontally (the live renderer drives it with the existing `CrawlService`
 *    rAF loop, masked to the clip region).
 *
 * Recreated 1:1 from the Claude Design `Crawl Widgets II.html` handoff
 * (variants 11–20), cropped from the 1920×1080 scene to the bottom bar. The
 * SVGs keep the original ABSOLUTE coordinates and just set `viewBox` to the bar
 * crop (`barLeft barTop cardWidth cardHeight`) — no re-basing.
 *
 * PURE-DATA: each design carries a {@link SerializableDesign} (`serializable`)
 * whose `frame` is the FIXED chrome markup and whose `scroll.segment` is one
 * looping text segment. The generic {@link renderDesign} / {@link
 * renderScrollSegment} turn that data into SVG — there are NO per-design
 * `build()`/`svg` functions, so a published design transports as data only. The
 * remaining `CrawlScrollSpec`/`barLeft`/… fields are pure-data SPRITE-PLACEMENT
 * metadata (clip region, baseline, font metrics) the Pixi renderer needs to size
 * + animate the strip — they are not SVG markup.
 *
 * SECURITY: the crawl text is USER-CONTROLLED → its interpolation is XML-escaped
 * + capped inside the renderer's single escaping choke-point. Colours/separators
 * are catalog literals (not user input).
 *
 * Conventions: every defs/gradient/filter/pattern/clipPath id is prefixed with
 * the design id (`${id}-crd-…`) so multiple bars can inline without collisions.
 */

import {
  renderDesign,
  renderScrollSegment,
} from "../entities/designs/renderDesign";
import type { SerializableDesign } from "../entities/designs/serializableDesign";
import { getInstalledDesign } from "../entities/designs/installedRegistry";

export type CrawlDesignCategory = "crawl";

export interface CrawlScrollSpec {
  /** Font-family stack for the scrolling text. */
  font: string;
  /** Design-space px size. */
  fontSize: number;
  /** "400" | "700" etc. */
  weight?: string;
  fontStyle?: "normal" | "italic";
  letterSpacing?: number;
  /** Main text colour. */
  color: string;
  /** Separator glyph shown between loops. */
  sep: string;
  /** Separator accent colour. */
  sepColor: string;
  /**
   * Optional 2nd separator colour. When set, consecutive separators alternate
   * `sepColor` / `sepColor2` (the static gallery render shows several segments,
   * so the alternation reads there; the live single-segment loop uses the
   * primary colour).
   */
  sepColor2?: string;
  /** Text baseline y (ABSOLUTE scene coords). */
  baselineY: number;
  /** Clip region (ABSOLUTE scene coords) the scroll is masked to. */
  clipX: number;
  clipY: number;
  clipW: number;
  clipH: number;
  /** Optional extra attrs on the scroll `<text>` (e.g. a glow filter ref). */
  textExtra?: string;
  /** Optional `<defs>` the scroll strip needs (already design-prefixed). */
  scrollDefs?: string;
}

export interface CrawlDesign {
  id: string;
  name: string;
  subtitle: string;
  category: CrawlDesignCategory;
  description: string;
  swatches: string[];
  /** Bar crop, in scene px. */
  barLeft: number;
  barTop: number;
  cardWidth: number;
  cardHeight: number;
  fonts: string[];
  scroll: CrawlScrollSpec;
  /**
   * Pure-data design. `serializable.frame` = the FIXED chrome's inner markup
   * (absolute coords, no `<svg>` wrapper — the entry fns wrap it). `serializable
   * .scroll.segment` = ONE looping text segment with `{{USER_TEXT}}` (escaped +
   * capped), `{{SEP}}` (escaped) and `{{SEP_COLOR}}` (the per-segment accent).
   */
  serializable: SerializableDesign;
}

/** Cap to `n` chars (defensive — bound the rasterised strip width). */
const cap = (s: string, n = 240): string => {
  const t = String(s ?? "");
  return t.length <= n ? t : t.slice(0, n);
};

const vb = (d: CrawlDesign): string =>
  `${d.barLeft} ${d.barTop} ${d.cardWidth} ${d.cardHeight}`;

const sx = (d: CrawlDesign): string => {
  const s = d.scroll;
  const fs = `font-family="${s.font}" font-size="${s.fontSize}"`;
  const w = s.weight ? ` font-weight="${s.weight}"` : "";
  const st = s.fontStyle ? ` font-style="${s.fontStyle}"` : "";
  const ls = s.letterSpacing ? ` letter-spacing="${s.letterSpacing}"` : "";
  return `${fs}${w}${st}${ls}`;
};

/**
 * Build a design's `serializable` payload from its chrome markup + scroll spec.
 * The chrome is fully static (no user/dynamic tokens) so `frame` is the chrome
 * verbatim; the looping segment carries the three dynamic slots.
 */
const makeSerializable = (
  meta: Omit<CrawlDesign, "serializable">,
  chrome: string,
): SerializableDesign => {
  const s = meta.scroll;
  return {
    id: meta.id,
    name: meta.name,
    subtitle: meta.subtitle,
    widgetType: "crawl",
    category: meta.category,
    description: meta.description,
    swatches: meta.swatches,
    cardWidth: meta.cardWidth,
    cardHeight: meta.cardHeight,
    fonts: meta.fonts,
    viewBox: vb(meta as CrawlDesign),
    frame: chrome,
    scroll: {
      // one segment: text␣␣␣␣ + coloured separator + ␣␣␣␣ (matches the legacy
      // `scrollSegment`). USER_TEXT is pre-capped (hard 240, no ellipsis) at the
      // entry-fn boundary; SEP/SEP_COLOR are catalog literals passed per segment.
      segment: `<tspan fill="${s.color}">{{USER_TEXT}}    </tspan><tspan fill="{{SEP_COLOR}}">{{SEP}}</tspan><tspan fill="${s.color}">    </tspan>`,
      slots: {
        USER_TEXT: { kind: "text", escape: true },
        SEP: { kind: "text", escape: true },
        SEP_COLOR: { kind: "color", fallback: s.sepColor },
      },
    },
  };
};

/** Assemble a full `CrawlDesign` from its metadata + chrome markup. */
const makeDesign = (
  meta: Omit<CrawlDesign, "serializable">,
  chrome: string,
): CrawlDesign => ({ ...meta, serializable: makeSerializable(meta, chrome) });

// ── 11 · Stock Ticker ──────────────────────────────────────────────────────
const stockDesign: CrawlDesign = makeDesign(
  {
    id: "stock",
    name: "Stock Ticker",
    subtitle: "Market tape",
    category: "crawl",
    description:
      "Financial tape — symbol chips, mono figures, a LIVE clock tag and coloured separators.",
    swatches: ["#06120c", "#16c784", "#ea3943", "#d8e0e6"],
    barLeft: 60,
    barTop: 924,
    cardWidth: 1800,
    cardHeight: 92,
    fonts: ["DM Mono"],
    scroll: {
      font: "DM Mono, monospace",
      fontSize: 30,
      weight: "500",
      color: "#d8e0e6",
      sep: "|",
      sepColor: "#3a4a44",
      baselineY: 978,
      clipX: 232,
      clipY: 936,
      clipW: 1500,
      clipH: 64,
    },
  },
  `
  <rect x="60" y="924" width="1800" height="92" fill="#06120c" stroke="#123" stroke-width="1.5"/>
  <line x1="60" y1="924" x2="1860" y2="924" stroke="#16c784" stroke-width="3"/>
  <rect x="80" y="944" width="140" height="52" rx="6" fill="#0c1f16"/>
  <circle cx="104" cy="970" r="7" fill="#16c784"/>
  <text x="120" y="978" font-family="DM Mono, monospace" font-weight="500" font-size="20" fill="#16c784">LIVE</text>`,
);

// ── 12 · Now Playing ───────────────────────────────────────────────────────
const musicDesign: CrawlDesign = makeDesign(
  {
    id: "music",
    name: "Now Playing",
    subtitle: "Music bar",
    category: "crawl",
    description:
      "Album-art square, EQ bars, a NOW PLAYING tag and a track crawl with a progress underline.",
    swatches: ["#120a1e", "#1db954", "#ffffff", "#b39ddb"],
    barLeft: 60,
    barTop: 924,
    cardWidth: 1800,
    cardHeight: 92,
    fonts: ["Inter"],
    scroll: {
      font: "Inter, sans-serif",
      fontSize: 30,
      weight: "700",
      color: "#ffffff",
      sep: "◆",
      sepColor: "#5a4a6e",
      baselineY: 976,
      clipX: 360,
      clipY: 936,
      clipW: 1380,
      clipH: 50,
    },
  },
  `
  <defs><linearGradient id="music-crd-art" x1="0" x2="1" y1="0" y2="1"><stop offset="0%" stop-color="#7d4cff"/><stop offset="100%" stop-color="#1db954"/></linearGradient></defs>
  <rect x="60" y="924" width="1800" height="92" rx="14" fill="#120a1e"/>
  <rect x="80" y="936" width="68" height="68" rx="8" fill="url(#music-crd-art)"/>
  <circle cx="114" cy="970" r="12" fill="#120a1e"/><circle cx="114" cy="970" r="4" fill="#fff"/>
  <g transform="translate(168 996)" fill="#1db954"><rect x="0" y="-20" width="6" height="20"/><rect x="10" y="-34" width="6" height="34"/><rect x="20" y="-12" width="6" height="12"/><rect x="30" y="-40" width="6" height="40"/><rect x="40" y="-26" width="6" height="26"/></g>
  <text x="168" y="954" font-family="Inter, sans-serif" font-weight="800" font-size="16" fill="#1db954" letter-spacing="2">NOW PLAYING</text>
  <rect x="360" y="1000" width="1480" height="3" fill="#2a1f3a"/>
  <rect x="360" y="1000" width="520" height="3" fill="#1db954"/>`,
);

// ── 13 · Sports Scorebar ───────────────────────────────────────────────────
const sportsDesign: CrawlDesign = makeDesign(
  {
    id: "sports",
    name: "Sports Scorebar",
    subtitle: "Match bug",
    category: "crawl",
    description:
      "ESPN-style score bug with team chips and score, then a scoreline crawl of other matches.",
    swatches: ["#0a1020", "#ffd200", "#e2241a", "#1f8a4c"],
    barLeft: 60,
    barTop: 924,
    cardWidth: 1800,
    cardHeight: 92,
    fonts: ["Anton", "DM Mono", "Rubik Mono One", "Space Grotesk"],
    scroll: {
      font: "Space Grotesk, sans-serif",
      fontSize: 30,
      color: "#dfe6f2",
      sep: "·",
      sepColor: "#4a5a7a",
      baselineY: 976,
      clipX: 540,
      clipY: 936,
      clipW: 1200,
      clipH: 64,
    },
  },
  `
  <rect x="60" y="924" width="1800" height="92" fill="#0a1020"/>
  <rect x="60" y="924" width="460" height="92" fill="#0e1530"/>
  <rect x="80" y="946" width="48" height="48" rx="6" fill="#e2241a"/><text x="104" y="980" text-anchor="middle" font-family="Anton, sans-serif" font-size="28" fill="#fff">RJA</text>
  <text x="150" y="980" font-family="Rubik Mono One, monospace" font-size="34" fill="#fff">2</text>
  <text x="200" y="978" font-family="DM Mono, monospace" font-size="22" fill="#8ea0cc">—</text>
  <text x="232" y="980" font-family="Rubik Mono One, monospace" font-size="34" fill="#fff">1</text>
  <rect x="272" y="946" width="48" height="48" rx="6" fill="#1f8a4c"/><text x="296" y="980" text-anchor="middle" font-family="Anton, sans-serif" font-size="28" fill="#fff">VRD</text>
  <rect x="340" y="952" width="120" height="36" rx="4" fill="#ffd200"/><text x="400" y="978" text-anchor="middle" font-family="DM Mono, monospace" font-weight="500" font-size="20" fill="#0a1020">73' EN VIVO</text>
  <polygon points="520,924 560,970 520,1016" fill="#0a1020"/>`,
);

// ── 14 · Vaporwave ─────────────────────────────────────────────────────────
const vaporDesign: CrawlDesign = makeDesign(
  {
    id: "vapor",
    name: "Vaporwave",
    subtitle: "Chrome strip",
    category: "crawl",
    description:
      "Sunset gradient bar with a chrome italic tag, grid baseline and ◆ separators in cyan/magenta.",
    swatches: ["#2a0a55", "#ff5fb0", "#2ce5ff", "#ffd24a"],
    barLeft: 60,
    barTop: 924,
    cardWidth: 1800,
    cardHeight: 92,
    fonts: ["Monoton", "Audiowide"],
    scroll: {
      font: "Audiowide, sans-serif",
      fontSize: 28,
      color: "#ffffff",
      sep: "◆",
      sepColor: "#2ce5ff",
      sepColor2: "#ff5fb0",
      baselineY: 972,
      clipX: 320,
      clipY: 936,
      clipW: 1420,
      clipH: 60,
    },
  },
  `
  <defs>
    <linearGradient id="vapor-crd-bg" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#2a0a55"/><stop offset="100%" stop-color="#7d1a8c"/></linearGradient>
    <linearGradient id="vapor-crd-chrome" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#fff"/><stop offset="50%" stop-color="#9be9ff"/><stop offset="100%" stop-color="#ff7ad0"/></linearGradient>
  </defs>
  <rect x="60" y="924" width="1800" height="92" fill="url(#vapor-crd-bg)" stroke="#ff5fb0" stroke-width="2"/>
  <g stroke="#ff2d95" stroke-width="1.5" opacity=".7"><line x1="60" y1="998" x2="1860" y2="998"/><line x1="60" y1="1010" x2="1860" y2="1010"/><line x1="200" y1="998" x2="160" y2="1016"/><line x1="500" y1="998" x2="470" y2="1016"/><line x1="900" y1="998" x2="900" y2="1016"/><line x1="1300" y1="998" x2="1330" y2="1016"/><line x1="1700" y1="998" x2="1740" y2="1016"/></g>
  <text x="84" y="976" font-family="Monoton, sans-serif" font-size="34" fill="url(#vapor-crd-chrome)" stroke="#2a0a55" stroke-width="0.8" font-style="italic">A E S</text>`,
);

// ── 15 · Tape Label ────────────────────────────────────────────────────────
const tapeDesign: CrawlDesign = makeDesign(
  {
    id: "tape",
    name: "Tape Label",
    subtitle: "Dymo embossed",
    category: "crawl",
    description:
      "Embossed label-maker strip with rounded uppercase characters and a red Dymo INFO tag.",
    swatches: ["#161513", "#c2342f", "#f3efe6", "#8a8a8a"],
    barLeft: 60,
    barTop: 930,
    cardWidth: 1800,
    cardHeight: 80,
    fonts: ["Space Grotesk"],
    scroll: {
      font: "Space Grotesk, sans-serif",
      fontSize: 30,
      weight: "700",
      letterSpacing: 4,
      color: "#e8e4da",
      sep: "◦",
      sepColor: "#e8e4da",
      baselineY: 978,
      clipX: 320,
      clipY: 936,
      clipW: 1420,
      clipH: 64,
    },
  },
  `
  <defs><linearGradient id="tape-crd-emb" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#2a2925"/><stop offset="50%" stop-color="#161513"/><stop offset="100%" stop-color="#0c0b0a"/></linearGradient></defs>
  <rect x="60" y="930" width="1800" height="80" rx="40" fill="url(#tape-crd-emb)"/>
  <rect x="60" y="930" width="1800" height="80" rx="40" fill="none" stroke="#3a3833" stroke-width="2"/>
  <rect x="84" y="942" width="200" height="56" rx="28" fill="#c2342f"/>
  <text x="184" y="980" text-anchor="middle" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="26" fill="#f3efe6" letter-spacing="4">INFO</text>`,
);

// ── 16 · Hologram HUD ──────────────────────────────────────────────────────
// The glow filter is applied separately to the chrome (`-crd-gc`) and the
// scroll strip (`-crd-g`) — they are two independent sprites at runtime, so a
// single shared filter (as in the static mock) isn't possible. Same blur on
// each layer; visually equivalent since chrome + scroll don't overlap. (Same
// for LED.) Intentional — do not try to "unify" it.
const holoDesign: CrawlDesign = makeDesign(
  {
    id: "holo",
    name: "Hologram HUD",
    subtitle: "Sci-fi readout",
    category: "crawl",
    description:
      "Translucent cyan HUD with corner brackets, a target-reticle tag and ▮ separators.",
    swatches: ["#021018", "#39e6ff", "#7af9c8", "#ff7ad0"],
    barLeft: 60,
    barTop: 924,
    cardWidth: 1800,
    cardHeight: 92,
    fonts: ["Orbitron", "DM Mono"],
    scroll: {
      font: "DM Mono, monospace",
      fontSize: 30,
      color: "#bff4ff",
      sep: "▮",
      sepColor: "#ff7ad0",
      sepColor2: "#7af9c8",
      baselineY: 978,
      clipX: 320,
      clipY: 936,
      clipW: 1420,
      clipH: 64,
      textExtra: ` filter="url(#holo-crd-g)"`,
      scrollDefs: `<filter id="holo-crd-g"><feGaussianBlur stdDeviation="1.6"/><feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter>`,
    },
  },
  `
  <defs><filter id="holo-crd-gc"><feGaussianBlur stdDeviation="1.6"/><feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
  <g filter="url(#holo-crd-gc)">
    <rect x="60" y="924" width="1800" height="92" fill="#021018" opacity=".82" stroke="#39e6ff" stroke-width="1.5"/>
    <g stroke="#39e6ff" stroke-width="2" fill="none"><path d="M60 950 V924 H86"/><path d="M1860 950 V924 H1834"/><path d="M60 990 V1016 H86"/><path d="M1860 990 V1016 H1834"/></g>
    <g transform="translate(120 970)"><circle r="22" fill="none" stroke="#39e6ff" stroke-width="2"/><line x1="-30" y1="0" x2="-22" y2="0" stroke="#39e6ff" stroke-width="2"/><line x1="22" y1="0" x2="30" y2="0" stroke="#39e6ff" stroke-width="2"/><line x1="0" y1="-30" x2="0" y2="-22" stroke="#39e6ff" stroke-width="2"/><circle r="4" fill="#7af9c8"/></g>
    <text x="160" y="978" font-family="Orbitron, sans-serif" font-weight="700" font-size="20" fill="#7af9c8" letter-spacing="2">HUD</text>
  </g>`,
);

// ── 17 · Kraft & Stamp ─────────────────────────────────────────────────────
const kraftDesign: CrawlDesign = makeDesign(
  {
    id: "kraft",
    name: "Kraft & Stamp",
    subtitle: "Parcel tape",
    category: "crawl",
    description:
      "Brown kraft-paper strip with a red rubber-stamp tag, typewriter text and ◉ separators.",
    swatches: ["#c89b5c", "#7a3b2e", "#2c2620", "#e8ddc4"],
    barLeft: 60,
    barTop: 924,
    cardWidth: 1800,
    cardHeight: 92,
    fonts: ["Anton", "DM Mono"],
    scroll: {
      font: "DM Mono, monospace",
      fontSize: 28,
      color: "#2c2620",
      sep: "◉",
      sepColor: "#7a3b2e",
      baselineY: 976,
      clipX: 332,
      clipY: 936,
      clipW: 1408,
      clipH: 64,
    },
  },
  `
  <defs><filter id="kraft-crd-grain" x="0" y="0" width="100%" height="100%"><feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" seed="7"/><feColorMatrix values="0 0 0 0 0.35  0 0 0 0 0.22  0 0 0 0 0.1  0 0 0 .1 0"/><feComposite in2="SourceGraphic" operator="in"/></filter></defs>
  <rect x="60" y="924" width="1800" height="92" fill="#c89b5c"/>
  <rect x="60" y="924" width="1800" height="92" fill="#7a3b2e" filter="url(#kraft-crd-grain)" opacity=".5"/>
  <line x1="60" y1="930" x2="1860" y2="930" stroke="#a8814a" stroke-width="2"/>
  <line x1="60" y1="1010" x2="1860" y2="1010" stroke="#a8814a" stroke-width="2"/>
  <g transform="translate(180 970) rotate(-6)"><rect x="-96" y="-30" width="192" height="60" rx="6" fill="none" stroke="#7a3b2e" stroke-width="3"/><text x="0" y="9" text-anchor="middle" font-family="Anton, sans-serif" font-size="30" fill="#7a3b2e" letter-spacing="3" opacity=".85">CORREO</text></g>`,
);

// ── 18 · Comic Pow ─────────────────────────────────────────────────────────
const comicDesign: CrawlDesign = makeDesign(
  {
    id: "comic",
    name: "Comic Pow",
    subtitle: "Burst tape",
    category: "crawl",
    description:
      "Halftone yellow strip with a starburst NEWS! tag, heavy black outline and ⚡ separators.",
    swatches: ["#ffd23f", "#ff3d4d", "#2b3a8c", "#161513"],
    barLeft: 60,
    barTop: 924,
    cardWidth: 1800,
    cardHeight: 92,
    fonts: ["Bungee"],
    scroll: {
      font: "Bungee, sans-serif",
      fontSize: 28,
      color: "#161513",
      sep: "⚡",
      sepColor: "#ff3d4d",
      sepColor2: "#2b3a8c",
      baselineY: 978,
      clipX: 360,
      clipY: 936,
      clipW: 1380,
      clipH: 64,
    },
  },
  `
  <defs><pattern id="comic-crd-dots" width="14" height="14" patternUnits="userSpaceOnUse"><circle cx="3" cy="3" r="2.2" fill="#ff3d4d"/></pattern></defs>
  <rect x="60" y="924" width="1800" height="92" fill="#ffd23f" stroke="#161513" stroke-width="5"/>
  <rect x="60" y="924" width="1800" height="92" fill="url(#comic-crd-dots)" opacity=".18"/>
  <g transform="translate(180 970)">
    <polygon points="0,-44 11,-16 42,-18 19,3 28,33 0,17 -28,33 -19,3 -42,-18 -11,-16" fill="#ff3d4d" stroke="#161513" stroke-width="4"/>
    <text x="0" y="6" text-anchor="middle" font-family="Bungee, sans-serif" font-size="18" fill="#fff">NEWS!</text>
  </g>`,
);

// ── 19 · Mono Outline ──────────────────────────────────────────────────────
// No fill — hairline rules + clean uppercase, floats over any scene.
const outlineDesign: CrawlDesign = makeDesign(
  {
    id: "outline",
    name: "Mono Outline",
    subtitle: "Hairline transparent",
    category: "crawl",
    description:
      "No fill — just a hairline outline rule and clean uppercase text with slash separators.",
    swatches: ["#000000", "#ffffff", "#9a9a9a", "#7fbfff"],
    barLeft: 60,
    barTop: 930,
    cardWidth: 1800,
    cardHeight: 80,
    fonts: ["Space Grotesk"],
    scroll: {
      font: "Space Grotesk, sans-serif",
      fontSize: 30,
      weight: "500",
      letterSpacing: 1,
      color: "#ffffff",
      sep: "/",
      sepColor: "#9a9a9a",
      baselineY: 980,
      clipX: 300,
      clipY: 938,
      clipW: 1440,
      clipH: 60,
    },
  },
  `
  <line x1="60" y1="930" x2="1860" y2="930" stroke="#fff" stroke-width="2"/>
  <line x1="60" y1="1010" x2="1860" y2="1010" stroke="#fff" stroke-width="2"/>
  <rect x="80" y="946" width="172" height="48" rx="0" fill="none" stroke="#fff" stroke-width="2"/>
  <circle cx="106" cy="970" r="6" fill="#7fbfff"/>
  <text x="124" y="978" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="22" fill="#fff" letter-spacing="3">LIVE</text>`,
);

// ── 20 · LED Matrix ────────────────────────────────────────────────────────
const ledDesign: CrawlDesign = makeDesign(
  {
    id: "led",
    name: "LED Matrix",
    subtitle: "Dot-board",
    category: "crawl",
    description:
      "Amber dot-matrix display with an unlit-dot grid, glowing characters and a stadium frame.",
    swatches: ["#0a0a0a", "#ff5a2a", "#2a0e06", "#ffb703"],
    barLeft: 56,
    barTop: 920,
    cardWidth: 1808,
    cardHeight: 100,
    fonts: ["VT323"],
    scroll: {
      font: "VT323, monospace",
      fontSize: 46,
      color: "#ff5a2a",
      sep: "*",
      sepColor: "#ffb703",
      baselineY: 986,
      clipX: 316,
      clipY: 936,
      clipW: 1424,
      clipH: 64,
      textExtra: ` filter="url(#led-crd-g)"`,
      scrollDefs: `<filter id="led-crd-g"><feGaussianBlur stdDeviation="1.4"/><feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter>`,
    },
  },
  `
  <defs>
    <pattern id="led-crd-dot" width="14" height="14" patternUnits="userSpaceOnUse"><circle cx="7" cy="7" r="3" fill="#2a0e06"/></pattern>
    <filter id="led-crd-gc"><feGaussianBlur stdDeviation="1.4"/><feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter>
  </defs>
  <rect x="56" y="920" width="1808" height="100" rx="10" fill="#1a1a1a"/>
  <rect x="68" y="930" width="1784" height="80" rx="4" fill="#0a0a0a"/>
  <rect x="68" y="930" width="1784" height="80" fill="url(#led-crd-dot)"/>
  <rect x="84" y="942" width="170" height="56" rx="4" fill="#1a0a04" stroke="#ff5a2a" stroke-width="1.5"/>
  <text x="169" y="982" text-anchor="middle" font-family="VT323, monospace" font-size="40" fill="#ffb703" filter="url(#led-crd-gc)">INFO</text>`,
);

export const CRAWL_DESIGNS: CrawlDesign[] = [
  stockDesign,
  musicDesign,
  sportsDesign,
  vaporDesign,
  tapeDesign,
  holoDesign,
  kraftDesign,
  comicDesign,
  outlineDesign,
  ledDesign,
];

// ── render entry points (pure-data via renderDesign / renderScrollSegment) ───

/** The fixed chrome (bar bg + tag) SVG — stretched to the widget bounds. */
export function renderCrawlChromeSvg(design: CrawlDesign): string {
  return `<svg viewBox="${vb(design)}" xmlns="http://www.w3.org/2000/svg">${renderDesign(design.serializable)}</svg>`;
}

/**
 * One scroll segment ("text␣␣␣␣sep␣␣␣␣") as escaped tspans, design-coloured.
 * `idx` selects the separator colour for designs that alternate (`sepColor2`).
 * The pure-data segment is filled via {@link renderScrollSegment}; USER_TEXT is
 * pre-capped here (hard 240, no ellipsis — matching the legacy cap) with a "•"
 * fallback for empty text, and the per-index separator colour is bound in.
 */
function scrollSegment(design: CrawlDesign, text: string, idx = 0): string {
  const s = design.scroll;
  const sc = idx % 2 === 1 && s.sepColor2 ? s.sepColor2 : s.sepColor;
  return renderScrollSegment(design.serializable, {
    values: {
      USER_TEXT: cap(text) || "•",
      SEP: s.sep,
      SEP_COLOR: sc,
    },
    // Texto del usuario: se escapa aunque un diseño de la comunidad declare
    // `escape:false` en el slot.
    untrusted: ["USER_TEXT"],
  });
}

/**
 * The animated SCROLL STRIP — one segment at its natural width, baseline-aligned
 * within a `stripWidth × cardHeight` viewBox starting at 0,0. The live renderer
 * measures the segment width, calls this, rasterises and animates the sprite.
 */
export function renderCrawlScrollSvg(
  design: CrawlDesign,
  text: string,
  stripWidth: number,
): string {
  const s = design.scroll;
  const by = s.baselineY - design.barTop;
  const w = Math.max(1, Math.round(stripWidth));
  const defs = s.scrollDefs ? `<defs>${s.scrollDefs}</defs>` : "";
  return `<svg viewBox="0 0 ${w} ${design.cardHeight}" xmlns="http://www.w3.org/2000/svg">${defs}<text x="0" y="${by}" ${sx(design)}${s.textExtra ?? ""}>${scrollSegment(design, text)}</text></svg>`;
}

/**
 * A FULL static bar (chrome + a few clipped scroll segments) for the Store
 * thumbnail + the config preview. Not used for the live (animated) render.
 */
export function renderCrawlDesignSvg(
  design: CrawlDesign,
  text: string,
): string {
  const s = design.scroll;
  const seg = scrollSegment(design, text);
  const clipId = `${design.id}-crd-staticclip`;
  const defs = s.scrollDefs ? s.scrollDefs : "";
  return `<svg viewBox="${vb(design)}" xmlns="http://www.w3.org/2000/svg">
  <defs><clipPath id="${clipId}"><rect x="${s.clipX}" y="${s.clipY}" width="${s.clipW}" height="${s.clipH}"/></clipPath>${defs}</defs>
  ${renderDesign(design.serializable)}
  <g clip-path="url(#${clipId})"><text x="${s.clipX + 12}" y="${s.baselineY}" ${sx(design)}${s.textExtra ?? ""}>${seg}${scrollSegment(design, text, 1)}${scrollSegment(design, text, 2)}</text></g>
</svg>`;
}

/**
 * Resolve a crawl design by id for the live Pixi renderer. First-party catalog
 * wins; otherwise, when the id is a community design the user installed (its
 * `options.designId` is a backend id), fall back to the installed-design
 * registry so the live widget renders it exactly like a first-party one.
 * Returns `undefined` for an absent/unknown id.
 */
export function getCrawlDesign(
  id: string | undefined,
): CrawlDesign | undefined {
  if (!id) {
    return undefined;
  }
  const local = CRAWL_DESIGNS.find((d) => d.id === id);
  if (local) {
    return local;
  }
  // Community designs are generic SerializableDesigns constrained to the
  // "crawl" widgetType here; the cast is safe (the renderer consumes the shared
  // shape plus the crawl sprite-placement metadata carried on the payload).
  return getInstalledDesign(id, "crawl") as CrawlDesign | undefined;
}
